import { useCallback, useEffect, useState } from "react";
import api, { rupiah, getErrorMessage, downloadFile } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Landmark, FileDown } from "lucide-react";
import BankSources from "./BankSources";

const STATUS_STYLE = {
  RECONCILED: "bg-green-100 text-green-800 border-green-300",
  CONTINUOUS: "bg-green-100 text-green-800 border-green-300",
  COMPLETE: "bg-green-100 text-green-800 border-green-300",
  READY_FOR_REVIEW: "bg-green-100 text-green-800 border-green-300",
  REVIEW_REQUIRED: "bg-amber-100 text-amber-800 border-amber-300",
  INCOMPLETE: "bg-red-100 text-red-800 border-red-300",
  PASS: "bg-green-100 text-green-800 border-green-300",
  PASS_WITH_REVIEW_ITEMS: "bg-amber-100 text-amber-800 border-amber-300",
  BLOCKED_OCR_REVIEW: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MISSING_PERIOD: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_MAPPING: "bg-red-100 text-red-800 border-red-300",
  BLOCKED_SOURCE: "bg-red-100 text-red-800 border-red-300",
};
const ACCEPTANCE_LABELS = {
  PASS: "Diterima",
  PASS_WITH_REVIEW_ITEMS: "Diterima dengan pos reviu",
  BLOCKED_OCR_REVIEW: "Tertahan — OCR belum direviu",
  BLOCKED_MISSING_PERIOD: "Tertahan — periode belum lengkap",
  BLOCKED_MAPPING: "Tertahan — pemetaan akun GL",
  BLOCKED_SOURCE: "Tertahan — sumber belum tersedia",
};
const ITEM_LABELS = {
  MATCHED_EXACT: "Cocok tepat",
  MATCHED_ONE_TO_MANY: "Satu ke banyak",
  MATCHED_MANY_TO_ONE: "Banyak ke satu",
  MATCHED_DAY_TOTAL: "Cocok total harian",
  MATCHED_PARTIAL: "Cocok sebagian",
  TIMING_DIFFERENCE: "Perbedaan waktu",
  BANK_ONLY: "Belum tercatat di GL",
  GL_ONLY: "Ada di GL, tidak ada di rekening koran",
  REVIEW_REQUIRED: "Perlu reviu",
};
const TABS = [
  ["statements", "Rekening Koran"],
  ["bank", "Transaksi Bank"],
  ["gl", "Transaksi GL"],
  ["matches", "Pencocokan"],
  ["items", "Pos Rekonsiliasi"],
  ["cutoff", "Cut-off"],
  ["evidence", "Bukti Audit"],
  ["transfers", "Transfer Antarbank"],
  ["ocr", "Reviu OCR"],
];

// Rekonsiliasi Bank — satu rekening, satu periode audit, berapa pun jumlah berkas rekening korannya.
export default function BankReconciliation({ id }) {
  const [accounts, setAccounts] = useState([]);
  const [selected, setSelected] = useState("");
  const [view, setView] = useState(null);
  const [tab, setTab] = useState("statements");
  const [domain, setDomain] = useState(null);
  const [transfers, setTransfers] = useState(null);
  const [refresh, setRefresh] = useState(0);

  const load = useCallback(() => {
    api.get(`/engagements/${id}/bank-accounts`)
      .then(({ data }) => {
        setAccounts(data);
        setSelected((current) => current || (data.length === 1 ? data[0].id : ""));
      })
      .catch(() => {});
  }, [id]);
  useEffect(() => { setSelected(""); setView(null); load(); }, [load]);

  // Ringkasan domain bank: setiap rekening yang disetujui punya status eksplisit, tidak ada yang
  // dianggap selesai secara diam-diam.
  useEffect(() => {
    api.get(`/engagements/${id}/bank-summary`).then(({ data }) => setDomain(data)).catch(() => setDomain(null));
    api.get(`/engagements/${id}/interbank-transfers`).then(({ data }) => setTransfers(data)).catch(() => setTransfers(null));
  }, [id, refresh]);

  useEffect(() => {
    if (!selected) { setView(null); return; }
    api.get(`/engagements/${id}/bank-accounts/${selected}/reconciliation`)
      .then(({ data }) => setView(data))
      .catch((e) => toast.error(getErrorMessage(e)));
  }, [id, selected, refresh]);

  const exportWorkpaper = () =>
    downloadFile(`/engagements/${id}/bank-accounts/${selected}/workpaper.xlsx`,
      `rekonsiliasi-bank-${selected}.xlsx`).catch((e) => toast.error(getErrorMessage(e)));

  const summary = view?.reconciliation_summary;
  return (
    <div className="space-y-4" data-testid="bank-reconciliation">
      <div className="bg-white border border-border rounded-sm p-4 flex flex-wrap items-end gap-3">
        <div className="flex items-center gap-2 font-head font-semibold text-sm mr-2"><Landmark size={15} />Rekonsiliasi Bank</div>
        <div><Label className="text-xs">Rekening bank</Label>
          <Select value={selected} onValueChange={setSelected}>
            <SelectTrigger className="h-9 w-72" data-testid="bank-select"><SelectValue placeholder="Pilih rekening bank" /></SelectTrigger>
            <SelectContent>{accounts.map((row) => (
              <SelectItem key={row.id} value={row.id}>{row.masked_name || row.name}</SelectItem>))}
            </SelectContent>
          </Select>
        </div>
        {view && (
          <Button size="sm" variant="outline" onClick={exportWorkpaper} data-testid="bank-export">
            <FileDown size={14} className="mr-1" />Ekspor Kertas Kerja
          </Button>
        )}
      </div>

      <BankSources id={id} selected={selected}
        onChanged={(accountId) => { load(); if (accountId) setSelected(accountId); setRefresh((n) => n + 1); }} />

      {domain && <DomainSummary domain={domain} onSelect={setSelected} selected={selected} />}

      {view && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="bank-summary">
            <Figure label="Periode audit" value={view.audit_period_display} />
            <Figure label="Rekening koran" value={`${view.statements.length} berkas`} />
            <Figure label="Kelengkapan" value={`${view.coverage?.covered || "—"} bulan`} />
            <Figure label="Kesinambungan" value={view.continuity?.status} status={view.continuity?.status} />
            <Figure label="Saldo awal" value={rupiah(view.totals?.bank_opening)} />
            <Figure label="Penerimaan" value={rupiah(view.totals?.bank_inflows)} />
            <Figure label="Pengeluaran" value={rupiah(view.totals?.bank_outflows)} />
            <Figure label="Saldo akhir" value={rupiah(view.totals?.bank_closing)} />
            <Figure label="Akun GL" value={view.gl_account?.account_code} />
            <Figure label="Saldo GL" value={rupiah(view.totals?.gl_closing_balance)} />
            <Figure label="Selisih GL vs rekening koran" value={rupiah(view.totals?.gl_vs_bank_closing_difference)} />
            <Figure label="Status" value={summary?.status} status={summary?.status} />
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs" data-testid="bank-match-counts">
            <Figure label="Dialokasikan" value={rupiah(summary?.allocated_amount)} />
            <Figure label="Belum cocok (bank)" value={`${summary?.unresolved_bank || 0} · ${rupiah(summary?.unresolved_bank_amount)}`} />
            <Figure label="Belum cocok (GL)" value={`${summary?.unresolved_gl || 0} · ${rupiah(summary?.unresolved_gl_amount)}`} />
            <Figure label="Perbedaan waktu" value={view.timing_differences ?? 0} />
          </div>

          <div className="bg-white border border-border rounded-sm">
            <div className="flex flex-wrap gap-1 border-b border-border px-2 py-1">
              {TABS.map(([key, label]) => (
                <button key={key} onClick={() => setTab(key)} data-testid={`bank-tab-${key}`}
                  className={`text-xs px-2 py-1 rounded-sm ${tab === key ? "bg-slate-200 font-medium" : "hover:bg-slate-100"}`}>
                  {label}
                </button>
              ))}
            </div>
            <div className="overflow-x-auto p-2" data-testid={`bank-panel-${tab}`}>
              {tab === "statements" && <Statements rows={view.statements} />}
              {tab === "bank" && <Items rows={view.bank_items} origin="bank" />}
              {tab === "gl" && <Items rows={view.gl_items} origin="gl" />}
              {tab === "matches" && <Matches rows={view.allocations} />}
              {tab === "items" && <Items rows={(view.bank_items || []).concat(view.gl_items || []).filter((r) => Number(r.remaining_amount) > 0)} origin="items" />}
              {tab === "cutoff" && <Items rows={(view.bank_items || []).filter((r) => r.cutoff && r.cutoff !== "IN_PERIOD")} origin="cutoff" />}
              {tab === "evidence" && <Evidence rows={view.evidence} />}
              {tab === "transfers" && <Transfers data={transfers} account={view.bank_account?.masked_name} />}
              {tab === "ocr" && <OcrReview rows={view.statements} />}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Figure({ label, value, status }) {
  return (
    <div className="border border-border rounded-sm p-2">
      <div className="text-muted-foreground">{label}</div>
      <div className={`font-medium tabular-nums ${status ? `inline-block mt-1 px-2 py-0.5 border rounded-sm ${STATUS_STYLE[status] || ""}` : ""}`}>
        {value ?? "—"}
      </div>
    </div>
  );
}

function Statements({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="bank-statements">
      <thead><tr><th>Periode</th><th>Berkas</th><th className="text-right">Transaksi</th>
        <th className="text-right">Saldo awal</th><th className="text-right">Penerimaan</th>
        <th className="text-right">Pengeluaran</th><th className="text-right">Saldo akhir</th>
        <th>Aritmetika</th><th>Konvensi dibuktikan</th></tr></thead>
      <tbody>{rows.map((row) => (
        <tr key={row.statement_id}>
          <td>{row.statement_id}</td><td>{row.source_pdf}</td>
          <td className="text-right tabular-nums">{row.transactions}</td>
          <td className="text-right tabular-nums">{rupiah(row.opening)}</td>
          <td className="text-right tabular-nums">{rupiah(row.inflows)}</td>
          <td className="text-right tabular-nums">{rupiah(row.outflows)}</td>
          <td className="text-right tabular-nums">{rupiah(row.closing)}</td>
          <td>{row.arithmetic_status}</td>
          <td>{row.convention_demonstrated ? `Ya (${row.convention_checkpoints})` : "Belum"}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Items({ rows = [], origin }) {
  return (
    <table className="w-full audit-table text-xs" data-testid={`bank-items-${origin}`}>
      <thead><tr><th>ID</th><th>Tanggal</th><th>Arah</th><th className="text-right">Jumlah</th>
        <th className="text-right">Cocok</th><th className="text-right">Sisa</th><th>Status</th>
        <th>Cut-off</th><th>Usulan kategori</th><th>Keterangan</th></tr></thead>
      <tbody>{rows.slice(0, 200).map((row) => (
        <tr key={`${row.origin}-${row.id}`}>
          <td>{row.id}</td><td>{row.date}</td>
          <td>{row.direction === "INFLOW" ? "Masuk" : "Keluar"}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.matched_amount)}</td>
          <td className="text-right tabular-nums">{rupiah(row.remaining_amount)}</td>
          <td>{ITEM_LABELS[row.status] || row.status}</td>
          <td>{row.cutoff || "—"}</td>
          <td>{row.suggested_category || "—"}</td>
          <td className="max-w-md truncate">{row.description}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Matches({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="bank-matches">
      <thead><tr><th>Rekening koran</th><th>Buku besar</th><th className="text-right">Jumlah</th>
        <th>Aturan</th><th className="text-right">Selisih hari</th></tr></thead>
      <tbody>{rows.slice(0, 200).map((row, index) => (
        <tr key={index}>
          <td>{row.bank_id}</td><td>{row.gl_id}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td>{ITEM_LABELS[row.rule] || row.rule}</td>
          <td className="text-right tabular-nums">{row.date_lag_days ?? 0}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function Evidence({ rows = [] }) {
  return (
    <table className="w-full audit-table text-xs" data-testid="bank-evidence">
      <thead><tr><th>Berkas</th><th>SHA-256</th><th className="text-right">Ukuran</th><th>Periode</th><th>Status reviu</th></tr></thead>
      <tbody>{rows.map((row) => (
        <tr key={row.sha256 || row.filename}>
          <td>{row.filename}</td>
          <td className="font-mono">{(row.sha256 || "").slice(0, 16)}…</td>
          <td className="text-right tabular-nums">{row.bytes}</td>
          <td>{row.period}</td><td>{row.review_status || "—"}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function DomainSummary({ domain, onSelect, selected }) {
  return (
    <div className="bg-white border border-border rounded-sm p-2 overflow-x-auto" data-testid="bank-domain-summary">
      <div className="text-xs font-head font-semibold px-1 pb-1">
        Ringkasan Domain Bank — {domain.total} rekening
        <span className="ml-2 font-normal text-muted-foreground">
          {Object.entries(domain.by_status || {}).map(([status, count]) =>
            `${ACCEPTANCE_LABELS[status] || status}: ${count}`).join(" · ")}
        </span>
      </div>
      <table className="w-full audit-table text-xs">
        <thead><tr><th>Rekening</th><th>Bank</th><th>Periode audit</th><th>Kelengkapan</th>
          <th className="text-right">Rekening koran</th><th>Metode ekstraksi</th><th>Aritmetika</th>
          <th>Konvensi</th><th>Kesinambungan</th><th>Akun GL</th><th className="text-right">Transaksi bank</th>
          <th className="text-right">Jurnal GL</th><th className="text-right">Cocok %</th>
          <th className="text-right">Belum cocok (bank)</th><th className="text-right">Belum cocok (GL)</th>
          <th className="text-right">Perbedaan waktu</th><th className="text-right">Cut-off</th>
          <th className="text-right">Bukti</th><th>Status penerimaan</th></tr></thead>
        <tbody>{(domain.accounts || []).map((row) => (
          <tr key={row.bank_account_id} onClick={() => onSelect(row.bank_account_id)}
            data-testid={`bank-domain-row-${row.bank_account_id}`}
            className={`cursor-pointer ${selected === row.bank_account_id ? "bg-slate-100" : "hover:bg-slate-50"}`}>
            <td>{row.masked_name}</td><td>{row.bank || "—"}</td><td>{row.audit_period || "—"}</td>
            <td>{row.coverage || "—"}</td>
            <td className="text-right tabular-nums">{row.statements}</td>
            <td>{(row.extraction_methods || []).join(", ") || "—"}</td>
            <td>{row.arithmetic}</td>
            <td>{row.convention_demonstrated ? "Terbukti" : "Belum"}</td>
            <td>{row.continuity}</td>
            <td>{row.gl_account || row.gl_mapping}</td>
            <td className="text-right tabular-nums">{row.bank_transactions}</td>
            <td className="text-right tabular-nums">{row.gl_entries}</td>
            <td className="text-right tabular-nums">{row.matched_percent === null ? "—" : `${row.matched_percent}%`}</td>
            <td className="text-right tabular-nums">{row.unresolved_bank}</td>
            <td className="text-right tabular-nums">{row.unresolved_gl}</td>
            <td className="text-right tabular-nums">{row.timing_differences}</td>
            <td className="text-right tabular-nums">{row.cutoff_items}</td>
            <td className="text-right tabular-nums">{row.evidence}</td>
            <td><span className={`inline-block px-2 py-0.5 border rounded-sm ${STATUS_STYLE[row.acceptance] || ""}`}>
              {ACCEPTANCE_LABELS[row.acceptance] || row.acceptance}</span></td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Transfers({ data, account }) {
  const rows = (data?.candidates || []).filter((row) =>
    !account || row.from_account === account || row.to_account === account);
  if (!rows.length) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="bank-transfers-empty">
      Tidak ada kandidat transfer antarbank pada rentang {data?.date_window ?? 2} hari.
    </div>;
  }
  return (
    <table className="w-full audit-table text-xs" data-testid="bank-transfers">
      <thead><tr><th>Dari rekening</th><th>Ke rekening</th><th className="text-right">Jumlah</th>
        <th>Tanggal keluar</th><th>Tanggal masuk</th><th className="text-right">Selisih hari</th><th>Status</th></tr></thead>
      <tbody>{rows.map((row, index) => (
        <tr key={index}>
          <td>{row.from_account}</td><td>{row.to_account}</td>
          <td className="text-right tabular-nums">{rupiah(row.amount)}</td>
          <td>{row.from_date}</td><td>{row.to_date}</td>
          <td className="text-right tabular-nums">{row.date_lag_days}</td>
          <td>Perlu konfirmasi auditor</td>
        </tr>))}
      </tbody>
    </table>
  );
}

function OcrReview({ rows = [] }) {
  const ocr = rows.filter((row) => row.extraction === "OCR");
  if (!ocr.length) {
    return <div className="text-xs text-muted-foreground p-2" data-testid="bank-ocr-empty">
      Seluruh rekening koran dibaca dari lapisan teks asli; OCR tidak digunakan.
    </div>;
  }
  return (
    <table className="w-full audit-table text-xs" data-testid="bank-ocr">
      <thead><tr><th>Periode</th><th>Berkas</th><th>Mesin OCR</th><th>Status reviu</th>
        <th>Pereviu</th><th>Dapat digunakan</th></tr></thead>
      <tbody>{ocr.map((row) => (
        <tr key={row.statement_id}>
          <td>{row.statement_id}</td><td>{row.source_pdf}</td><td>{row.ocr_engine || "—"}</td>
          <td>{row.ocr_status || "OCR_PENDING_REVIEW"}</td>
          <td>{row.ocr_reviewer || "—"}</td>
          <td>{row.usable ? "Ya" : "Belum — hasil OCR belum direviu auditor"}</td>
        </tr>))}
      </tbody>
    </table>
  );
}
